import { useState, useRef } from "react";
import { Box, Text } from "@chakra-ui/react";

// Generic free-form tag input (values: string[]) -- Enter/blur commits the typed text as a tag.
// Optional `suggestions` renders clickable chips below the input (e.g. values already seen in a
// source column); clicking one adds it the same way typing + Enter would. Used by StepLeadSource.jsx
// for the "kolumna decyzyjna" allowed-values picker, where the user needs both "pick from what's
// currently in the sheet" and "type a value that doesn't exist there yet" in one control.
const TagsInput = ({ values, onChange, placeholder, suggestions, borderColor = "gray.200" }) => {
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef(null);

    const addValue = (raw) => {
        const trimmed = raw.trim();
        if (!trimmed) return;
        if (values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) return;
        onChange([...values, trimmed]);
    };

    const commit = () => {
        addValue(inputValue);
        setInputValue("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(); }
    };

    const availableSuggestions = (suggestions ?? []).filter(
        (s) => !values.some((v) => v.toLowerCase() === s.toLowerCase())
    );

    return (
        <Box w="full">
            <Box
                bg="white"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="l2"
                p={2}
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                alignItems="center"
                cursor="text"
                onClick={() => inputRef.current?.focus()}
                _focusWithin={{ borderColor: "purple.400" }}
                minH="10"
                w="full"
            >
                {values.map((value, i) => (
                    <Box
                        key={i}
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                        px={2}
                        py={0.5}
                        bg="purple.50"
                        color="purple.700"
                        borderRadius={4}
                        fontSize="xs"
                    >
                        <Text>{value}</Text>
                        <Box
                            as="button"
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onChange(values.filter((_, j) => j !== i)); }}
                            lineHeight={1}
                            color="purple.500"
                            _hover={{ color: "purple.800" }}
                            fontSize="sm"
                        >
                            ×
                        </Box>
                    </Box>
                ))}
                <Box
                    as="input"
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={commit}
                    placeholder={values.length === 0 ? placeholder : undefined}
                    bg="transparent"
                    color="gray.900"
                    fontSize="sm"
                    border="none"
                    outline="none"
                    flex="1"
                    minW="160px"
                    style={{ color: "#17151F", background: "transparent" }}
                />
            </Box>
            {availableSuggestions.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
                    {availableSuggestions.map((s) => (
                        <Box
                            key={s}
                            as="button"
                            type="button"
                            onClick={() => addValue(s)}
                            px={2}
                            py={0.5}
                            bg="gray.50"
                            color="gray.500"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius={4}
                            fontSize="xs"
                            _hover={{ color: "purple.600", borderColor: "purple.400" }}
                        >
                            + {s}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default TagsInput;
